import React, {
	ChangeEvent,
	FormEvent,
	useEffect,
	useReducer,
	useState,
} from 'react';
import {
	BearingCalculationData,
	BearingTypeCode,
	CalculateDesignatedWidthPayload,
	ClearanceCalculationPayload,
	ClearanceWidthDescriptor,
	TimkenBearingMaterial,
	TimkenBearingMounting,
	TimkenUnit,
	TimkenUserMessage,
} from '../../../components/bearing-search/types/types';
import { BearingPartState } from '../../../components/bearing-search/reducers/reducer';
import clearanceCalculationsReducer, {
	ClearanceFormState,
	getInitialClearanceCalculationState,
} from './reducer';
import {
	clearanceWidthDescriptors,
	mountingOptions,
	text,
	unitMap,
} from '../../data';
import {
	bearingMaterials,
	getClearanceCalculation,
	getRadialInternalClearance,
	getUnmountedClearance,
	getUnmountedClearanceACBB,
	getCalculateDesignatedWidth,
	CLEARANCE_FIT_MIN,
	CLEARANCE_FIT_MAX,
} from '../../../components/bearing-search/api/api';
import CalculationResults from '../../components/CalculationResults';
import {
	getTooltip,
	toggleImportantNote,
	toggleNextTools,
	toggleReportReady,
} from '../../utils/engineering-tools';
import NumberInput from '../../../components/bearing-search/components/forms/NumberInput';
import SelectDropdown from '../../../components/bearing-search/components/forms/SelectDropdown';
import { FittingCalculationResults, SelectOption } from '../../types/types';
import { trackResults, updateActionParams } from '../../utils/analytics';
import { isValidBoundary, reorderRadialClearance, acbbRadialClearanceValues } from '../../utils/validations';

interface ClearanceFormProps {
	unit: TimkenUnit;
	bearing: BearingPartState;
	onClear: () => void;
	fittings?: Partial<FittingCalculationResults>;
}

const ClearanceForm: React.FC<ClearanceFormProps> = ( {
	unit,
	bearing,
	fittings,
	onClear,
} ) => {
	const [ isValidated, setIsValidated ] = useState( false );
	const [ isCalculated, setIsCalculated ] = useState( false );
	const [ errorMessages, setErrorMessages ] = useState<TimkenUserMessage[]>( [] );
	const [ isResultsLoading, setIsResultsLoading ] = useState( false );
	const [ materialList, setMaterialList ] = useState<SelectOption<string>[]>( [] );
	const [ isRadialClearanceLoading, setIsRadialClearanceLoading ] =
		useState( false );
	const [ isUnmountedClearanceLoading, setIsUnmountedClearanceLoading ] =
		useState( false );
	const [ isDesignatedWidthLoading, setIsDesignatedWidthLoading ] =
		useState( false );
	const [ radialClearanceOptions, setRadialClearanceOptions ] = useState<
		SelectOption<string>[]
	>([]);
	const [results, setResults] = useState<Array<BearingCalculationData | null>>(
		[],
	);
	const initialState = getInitialClearanceCalculationState(bearing, unit);
	const [formState, dispatch] = useReducer(
		clearanceCalculationsReducer,
		initialState,
	);

	useEffect( () => {
		// Build materials options
		setMaterialList(
			bearingMaterials.map( ( material: TimkenBearingMaterial ) => {
				return {
					id: `${ material.name.toLowerCase() }_material`,
					value: material.name,
					label: material.name,
				} as SelectOption<string>;
			} ),
		);

		selectMaterial( bearingMaterials[ bearingMaterials.length - 1 ] );

		// Called to make validation has the proper data it needs otherwise the error messages may have incorrect data
		handleFormChange();
	}, [] );

	useEffect(() => {
		setIsValidated(false);

		dispatch({
			type: 'change_unit',
			payload: unit,
		});

		dispatch({
			type: 'update_request_data',
		});
	}, [unit]);

	useEffect(() => {
		setIsValidated(false);

		if (fittings) {
			const properties: Array<keyof ClearanceFormState> = [
				'minimumOuterFit',
				'maximumOuterFit',
				'minimumInnerFit',
				'maximumInnerFit',
			];

			properties.forEach((field) => {
				const value = fittings[field];

				if (value) {
					dispatch({
						type: 'change_number_field',
						payload: [field, value],
					});
				}
			});
		}

		dispatch({
			type: 'update_request_data',
		});
	}, [fittings]);

	useEffect(() => {
		const {bearingType, partNumber} = bearing;

		if (bearingType !== BearingTypeCode.TRB) {
			if (bearingType === BearingTypeCode.ACBB) {
				setIsUnmountedClearanceLoading(true);
				setIsRadialClearanceLoading(true);

				getUnmountedClearanceACBB({
					bearingType,
					unit,
					partNumber,
				})
					.then((response) => {
						dispatch({
							type: 'change_number_field',
							payload: [
								'unmountedClearanceyMinimum',
								response.minUnmountedClearance,
							],
						});

						dispatch({
							type: 'change_number_field',
							payload: [
								'unmountedClearanceyMaximum',
								response.maxUnmountedClearance,
							],
						});

						const radialClearanceResponse = acbbRadialClearanceValues(bearingType);
						setRadialClearanceOptions(
							radialClearanceResponse.map((option) => {
								const value = option.clearanceLabel.trim();

								if (option.isDefault) {
									dispatch({
										type: 'change_radial_clearance',
										payload: value,
									});
								}

								return {
									id: `clearance_label_${value}`,
									label: value,
									value,
								};
							}),
						);
					})
					.finally(() => {
						setIsUnmountedClearanceLoading(false);
						setIsRadialClearanceLoading(false);
					});
			} else {
				setIsRadialClearanceLoading(true);
				setIsUnmountedClearanceLoading(true);

				getRadialInternalClearance({
					bearingType,
				})
					.then((response) => {
						const radialClearanceResponse = reorderRadialClearance(response.radialClearanceResponse, bearingType);
						setRadialClearanceOptions(
							radialClearanceResponse.map((option) => {
								const value = option.clearanceLabel.trim();

								if (option.isDefault) {
									dispatch({
										type: 'change_radial_clearance',
										payload: value,
									});
								}

								return {
									id: `clearance_label_${value}`,
									label: value,
									value,
								};
							}),
						);
					})
					.finally(() => {
						setIsRadialClearanceLoading(false);
					});
			}
		}
	}, [bearing.bearingType]);

	useEffect(() => {
		const radialClearanceValue = formState.radialInternalClearance;
		const {bearingType, partNumber} = bearing;

		if (radialClearanceValue) {
			setIsUnmountedClearanceLoading(true);

			if (bearingType === BearingTypeCode.ACBB && radialClearanceValue === 'Default') {
				getUnmountedClearanceACBB({
					bearingType,
					unit,
					partNumber,
				})
					.then((response) => {
						dispatch({
							type: 'change_number_field',
							payload: [
								'unmountedClearanceyMinimum',
								response.minUnmountedClearance,
							],
						});

						dispatch({
							type: 'change_number_field',
							payload: [
								'unmountedClearanceyMaximum',
								response.maxUnmountedClearance,
							],
						});
					})
					.finally(() => {
						setIsUnmountedClearanceLoading(false);
					});
			} else {
				getUnmountedClearance({
					bearingType,
					unit,
					partNumber,
					radialClearanceValue,
				})
					.then((response) => {
						dispatch({
							type: 'change_number_field',
							payload: ['unmountedClearanceyMinimum', response.clearanceMinimum],
						});

						dispatch({
							type: 'change_number_field',
							payload: ['unmountedClearanceyMaximum', response.clearanceMaximum],
						});
					})
					.finally(() => {
						setIsUnmountedClearanceLoading(false);
					});
			}
		}
	}, [unit, formState.radialInternalClearance]);

	useEffect(() => {
		handleFormChange();
	}, [
		formState.unmountedClearanceyMinimum,
		formState.unmountedClearanceyMaximum,
	]);

	// Show / hide recommended tools and important notes
	useEffect(() => {
		const isShown = isCalculated && ! isResultsLoading;
		toggleNextTools(isShown);
		toggleImportantNote(isShown);
		toggleReportReady(
			isShown,
			bearing.marketPlaceDescription,
			bearing.primaryImage,
		);

		return () => {
			toggleNextTools(false);
			toggleImportantNote(false);
		};
	}, [isCalculated, isResultsLoading]);

	const selectMaterial = ( material?: TimkenBearingMaterial ) => {
		if ( material ) {
			dispatch( {
				type: 'change_material',
				payload: material,
			} );
		}
	};

	useEffect( () => {
		setIsValidated(false);
		const descriptor = formState.designatedWidthDescriptor;
		const designators = {
			'coneBackFaceToConeBackFaceWidth': 1,
			'coneFrontfacetoConeFrontfaceWidth': 2,
			'cupBackFaceToCupBackFaceWidth': 3,
			'cupFrontfacetoCupFrontfaceWidth': 4
		};
		const { bearingType, bearingSubType, partNumber, cupPartNumber } =
			bearing;
		if (unit === undefined || unit === null) {
			unit = formState.unit;
		}
		const request: Partial<CalculateDesignatedWidthPayload> = {
			bearingType,
			bearingSubType,
			partNumber,
			cupPartNumber,
			bearingMounting: formState.bearingMounting,
			unit,
		};
		setIsDesignatedWidthLoading(true);
		getCalculateDesignatedWidth(request, designators[descriptor])
			.then( (response) => {
				dispatch( {
					type: 'change_width_field',
					payload: [ response ],
				} );
				dispatch({
					type: 'update_request_data',
				});
			}).finally( () => setIsDesignatedWidthLoading(false));
	}, [unit, formState.bearingMounting, formState.designatedWidthDescriptor]);

	const handleFormChange = () => {
		setIsValidated(false);

		dispatch({
			type: 'update_request_data',
		});
	};

	const handleSubmit = (event: FormEvent) => {
		event.preventDefault();
		setIsValidated(true);

		if (formState.requestData) {
			setIsCalculated(true);
			setIsResultsLoading(true);
			setErrorMessages([]);

			const {bearingType, bearingSubType, partNumber, cupPartNumber} =
				bearing;

			const request: Partial<ClearanceCalculationPayload> = {
				...formState.requestData,
				bearingType,
				bearingSubType,
				partNumber,
				cupPartNumber,
			};

			getClearanceCalculation(request)
				.then((response) => {
					updateActionParams('bearingClearanceCalculations', bearing);

					let reportData: Array<BearingCalculationData | null> = [];

					if (response.report && response.report.length > 0) {
						reportData = response.report[0].details;
					}

					if (response.header.messages?.length) {
						setErrorMessages(response.header.messages);
					}

					setResults(reportData);
					trackResults('bearingClearanceCalculations', request, reportData.length, bearing.marketPlaceDescription);

					document.querySelector('.t-engineering-tools__results').scrollIntoView({
						behavior: 'smooth',
					});
				})
				.finally(() => setIsResultsLoading(false));
		}
	};

	const clearForm = () => {
		setIsValidated(false);
		setIsCalculated(false);

		dispatch({
			type: 'reset',
			payload: initialState,
		});

		selectMaterial( bearingMaterials[ bearingMaterials.length - 1 ] );

		// Called to make validation has the proper data it needs otherwise the error messages may have incorrect data
		handleFormChange();

		onClear();
	};

	return (
		<div>
			<form onSubmit={handleSubmit} onChange={handleFormChange}>
				<div className="t-engineering-tools__constraints">
					<h3>{text.bearingClearance}</h3>
				</div>

				<div className="row form-row">
					<div className="col-md-2">
						<NumberInput
							id="clearance_calculation_shaft_id"
							required={true}
							label={text.shaftInnerDiameterDS}
							tooltip={getTooltip(text.shaftInnerDiameterDS)}
							unitLabel={unitMap[unit].length}
							value={formState.shaftID}
							invalid={isValidated && !formState.validation.get('shaftID')}
							errorMessage={`${text.shaftIdErrorLessThanBearingBore} ${formState.bearingID[unit]}`}
							min={0}
							onChange={(event: ChangeEvent<HTMLInputElement>) =>
								dispatch({
									type: 'change_number_field',
									payload: ['shaftID', event.target.value],
								})
							}
						/>
					</div>

					<div className="col-md-2">
						<NumberInput
							id="clearance_calculation_shaft_temperature"
							name="shaftTemperature"
							label={text.shaftTemperature}
							unitLabel={unitMap[unit].temperature}
							value={formState.shaftTemperature}
							required={true}
							invalid={
								isValidated && !formState.validation.get('shaftTemperature')
							}
							errorMessage={text.errorRequired}
							onChange={(event: ChangeEvent<HTMLInputElement>) =>
								dispatch({
									type: 'change_number_field',
									payload: ['shaftTemperature', event.target.value],
								})
							}
						/>
					</div>

					<div className="col-md-2">
						<NumberInput
							id="clearance_calculation_housing_od"
							required={true}
							label={text.housingOD}
							tooltip={getTooltip(text.housingOD)}
							unitLabel={unitMap[unit].length}
							value={formState.housingOD}
							invalid={isValidated && !formState.validation.get('housingOD')}
							errorMessage={`${text.housingOdErrorGreaterThanBearingOd} ${formState.bearingOD[unit]}`}
							min={0}
							onChange={(event: ChangeEvent<HTMLInputElement>) =>
								dispatch({
									type: 'change_number_field',
									payload: ['housingOD', event.target.value],
								})
							}
						/>
					</div>

					<div className="col-md-2">
						<NumberInput
							id="clearance_calculation_housing_temperature"
							name="housingTemperature"
							label={text.housingTemperature}
							unitLabel={unitMap[unit].temperature}
							value={formState.housingTemperature}
							required={true}
							invalid={
								isValidated && !formState.validation.get('housingTemperature')
							}
							errorMessage={text.errorRequired}
							onChange={(event: ChangeEvent<HTMLInputElement>) =>
								dispatch({
									type: 'change_number_field',
									payload: ['housingTemperature', event.target.value],
								})
							}
						/>
					</div>

					<div className="col-md-2">
						<SelectDropdown
							id="material"
							name="material"
							required={true}
							label={text.housingMaterial}
							value={formState.material}
							options={materialList}
							onChange={(event: ChangeEvent<HTMLSelectElement>) =>
								dispatch({
									type: 'change_material',
									payload: bearingMaterials.find(
										(material) => material.name === event.target.value,
									) as TimkenBearingMaterial,
								})
							}
						/>
					</div>
				</div>

				{bearing.bearingType === BearingTypeCode.TRB && (
					<>
						<div className="row form-row">
							<div className="col-md-3">
								<NumberInput
									id="clearance_calculation_bench_end_play"
									name="benchEndPlay"
									label={text.benchEndPlay}
									tooltip={getTooltip(text.benchEndPlay)}
									unitLabel={unitMap[unit].length}
									value={formState.benchEndPlay}
									required={true}
									invalid={
										isValidated && !formState.validation.get('benchEndPlay')
									}
									errorMessage={text.errorRequired}
									onChange={(event: ChangeEvent<HTMLInputElement>) =>
										dispatch({
											type: 'change_number_field',
											payload: ['benchEndPlay', event.target.value],
										})
									}
								/>
							</div>
							<div className="col-md-3">
								<NumberInput
									id="clearance_calculation_spacer_width_tolerance"
									name="spacerWidthTolerance"
									label={text.spacerWidthTolerance}
									unitLabel={unitMap[unit].length}
									tooltip={getTooltip(text.spacerWidthTolerance)}
									value={formState.spacerWidthTolerance}
									required={true}
									invalid={
										isValidated &&
										!formState.validation.get('spacerWidthTolerance')
									}
									errorMessage={text.errorRequired}
									onChange={(event: ChangeEvent<HTMLInputElement>) =>
										dispatch({
											type: 'change_number_field',
											payload: ['spacerWidthTolerance', event.target.value],
										})
									}
								/>
							</div>
						</div>

						{(bearing.bearingSubType === 'TS' ||
							bearing.bearingSubType === 'TSF') && (
							<div className="row form-row">
								<div className="col-md-3">
									<SelectDropdown
										id="clearance_calculation_mounting"
										name="bearingMounting"
										required={true}
										label={text.bearingMounting}
										value={formState.bearingMounting}
										options={mountingOptions}
										onChange={(event: ChangeEvent<HTMLSelectElement>) =>
											dispatch({
												type: 'change_mounting',
												payload: event.target.value as TimkenBearingMounting,
											})
										}
									/>
								</div>

								<div className="col-md-4">
									<SelectDropdown
										id="clearance_calculation_designated_width_descriptor"
										required={true}
										label={text.designatedWidthDescriptor}
										value={formState.designatedWidthDescriptor}
										options={clearanceWidthDescriptors}
										onChange={(event: ChangeEvent<HTMLSelectElement>) =>
											dispatch({
												type: 'change_designated_width_descriptor',
												payload: event.target.value as ClearanceWidthDescriptor,
											})
										}
									/>
								</div>

								<div className="col-md-2">
									<div className="t-engineering-tools__clearance-thumb">
										<img
											src={formState.designatedWidthImage}
											alt={text.clearanceImage}
										/>
									</div>
								</div>

								<div className="col-md-2">
									<NumberInput
										id="clearance_calculation_designated_width"
										label={text.designatedWidth}
										unitLabel={unitMap[unit].length}
										value={formState.designatedWidth}
										loading={isDesignatedWidthLoading}
										min={0}
										required={true}
										invalid={
											isValidated &&
											!formState.validation.get('designatedWidth')
										}
										errorMessage={text.errorNonZero}
										onChange={(event: ChangeEvent<HTMLInputElement>) =>
											dispatch({
												type: 'change_number_field',
												payload: ['designatedWidth', event.target.value],
											})
										}
									/>
								</div>
							</div>
						)}
					</>
				)}

				{bearing.bearingType !== BearingTypeCode.TRB && (
					<div className="row form-row">
						<div className="col-md-3">
							<SelectDropdown
								id="clearance_calculation_radial_clearance"
								required={true}
								label={text.radialInternalClearance}
								tooltip={getTooltip(text.radialInternalClearance)}
								value={formState.radialInternalClearance}
								options={radialClearanceOptions}
								loading={isRadialClearanceLoading}
								onChange={(event: ChangeEvent<HTMLSelectElement>) =>
									dispatch({
										type: 'change_radial_clearance',
										payload: event.target.value,
									})
								}
							/>
						</div>

						<div className="col-md-3">
							<NumberInput
								id="clearance_calculation_unmounted_clearance_min"
								name="unmountedClearanceyMinimum"
								label={text.unmountedClearanceyMinimum}
								unitLabel={unitMap[unit].length}
								value={formState.unmountedClearanceyMinimum}
								required={true}
								disabled={formState.isDisabled}
								loading={isUnmountedClearanceLoading}
								invalid={
									isValidated &&
									!formState.validation.get('unmountedClearanceyMinimum')
								}
								errorMessage={
									isValidBoundary(
										formState.unmountedClearanceyMinimum,
										formState.unmountedClearanceyMaximum,
									)
										? text.errorRequired
										: text.errorBoundaryLower
								}
								onChange={(event: ChangeEvent<HTMLInputElement>) =>
									dispatch({
										type: 'change_number_field',
										payload: ['unmountedClearanceyMinimum', event.target.value],
									})
								}
							/>
						</div>

						<div className="col-md-3">
							<NumberInput
								id="clearance_calculation_unmounted_clearance_max"
								name="unmountedClearanceyMaximum"
								label={text.unmountedClearanceyMaximum}
								unitLabel={unitMap[unit].length}
								value={formState.unmountedClearanceyMaximum}
								required={true}
								loading={isUnmountedClearanceLoading}
								disabled={formState.isDisabled}
								invalid={
									isValidated &&
									!formState.validation.get('unmountedClearanceyMaximum')
								}
								errorMessage={
									isValidBoundary(
										formState.unmountedClearanceyMinimum,
										formState.unmountedClearanceyMaximum,
									)
										? text.errorRequired
										: text.errorBoundaryUpper
								}
								onChange={(event: ChangeEvent<HTMLInputElement>) =>
									dispatch({
										type: 'change_number_field',
										payload: ['unmountedClearanceyMaximum', event.target.value],
									})
								}
							/>
						</div>
					</div>
				)}

				<div className="row form-row">
					<div className="col-md-2">
						<NumberInput
							id="clearance_calculation_inner_fits_min"
							name="minimumInnerFit"
							label={text.minimumInnerFit}
							unitLabel={unitMap[unit].length}
							tooltip={getTooltip(text.minimumInnerFit)}
							value={formState.minimumInnerFit}
							required={true}
							min={CLEARANCE_FIT_MIN}
							max={CLEARANCE_FIT_MAX}
							invalid={
								isValidated && !formState.validation.get('minimumInnerFit')
							}
							errorMessage={
								isValidBoundary(
									formState.minimumInnerFit,
									formState.maximumInnerFit,
								)
									? text.errorRequired
									: text.errorBoundaryLowerTightLoose
							}
							onChange={(event: ChangeEvent<HTMLInputElement>) =>
								dispatch({
									type: 'change_number_field',
									payload: ['minimumInnerFit', event.target.value],
								})
							}
						/>
					</div>

					<div className="col-md-2">
						<NumberInput
							id="clearance_calculation_inner_fits_max"
							name="maximumInnerFit"
							label={text.maximumInnerFit}
							unitLabel={unitMap[unit].length}
							tooltip={getTooltip(text.maximumInnerFit)}
							value={formState.maximumInnerFit}
							required={true}
							min={CLEARANCE_FIT_MIN}
							max={CLEARANCE_FIT_MAX}
							invalid={
								isValidated && !formState.validation.get('maximumInnerFit')
							}
							errorMessage={
								isValidBoundary(
									formState.minimumInnerFit,
									formState.maximumInnerFit,
								)
									? text.errorRequired
									: text.errorBoundaryUpperLooseTight
							}
							onChange={(event: ChangeEvent<HTMLInputElement>) =>
								dispatch({
									type: 'change_number_field',
									payload: ['maximumInnerFit', event.target.value],
								})
							}
						/>
					</div>

					<div className="col-md-2">
						<NumberInput
							id="clearance_calculation_outer_fits_min"
							name="minimumOuterFit"
							label={text.minimumOuterFit}
							unitLabel={unitMap[unit].length}
							tooltip={getTooltip(text.minimumOuterFit)}
							value={formState.minimumOuterFit}
							required={true}
							min={CLEARANCE_FIT_MIN}
							max={CLEARANCE_FIT_MAX}
							invalid={
								isValidated && !formState.validation.get('minimumOuterFit')
							}
							errorMessage={
								isValidBoundary(
									formState.minimumOuterFit,
									formState.maximumOuterFit,
								)
									? text.errorRequired
									: text.errorBoundaryLowerTightLoose
							}
							onChange={(event: ChangeEvent<HTMLInputElement>) =>
								dispatch({
									type: 'change_number_field',
									payload: ['minimumOuterFit', event.target.value],
								})
							}
						/>
					</div>

					<div className="col-md-2">
						<NumberInput
							id="clearance_calculation_outer_fits_max"
							name="maximumOuterFit"
							label={text.maximumOuterFit}
							unitLabel={unitMap[unit].length}
							tooltip={getTooltip(text.maximumOuterFit)}
							value={formState.maximumOuterFit}
							required={true}
							min={CLEARANCE_FIT_MIN}
							max={CLEARANCE_FIT_MAX}
							invalid={
								isValidated && !formState.validation.get('maximumOuterFit')
							}
							errorMessage={
								isValidBoundary(
									formState.minimumOuterFit,
									formState.maximumOuterFit,
								)
									? text.errorRequired
									: text.errorBoundaryUpperLooseTight
							}
							onChange={(event: ChangeEvent<HTMLInputElement>) =>
								dispatch({
									type: 'change_number_field',
									payload: ['maximumOuterFit', event.target.value],
								})
							}
						/>
					</div>
				</div>

				<div>
					<div className="t-engineering-tools__terms-of-use"
						 dangerouslySetInnerHTML={{__html: text.calculateTermsOfUse}}
					/>
					<div className="t-engineering-tools__search-actions">
						<button
							className="btn btn--primary"
							type="submit"
							disabled={isValidated}
						>
							{text.calculate}
						</button>

						<button
							className="btn btn--secondary"
							type="button"
							onClick={() => clearForm()}
						>
							{text.clear}
						</button>
					</div>
				</div>
			</form>

			{isCalculated && (
				<CalculationResults data={results} loading={isResultsLoading} serverErrors={errorMessages}/>
			)}
		</div>
	);
};

export default ClearanceForm;
